import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useApi } from "../api";
import { Card, ErrorNotice, Loading, PageHeader } from "./Ui";
type Role = "user" | "admin" | "super_admin";
type Policy = { version: number; roles: Record<Role, string[]> };
const labels: Record<string, string> = {
  "initiatives.create": "Create and submit initiatives",
  "executive.view": "View and export executive / management reports",
  "governance.view": "View management and Board escalation details",
  "governance.manage":
    "Review and approve escalations; send in-app notifications",
  "audit.view": "View audit records including governance decisions",
  "capacity.view": "View team delivery summaries",
  "delivery.individual.view": "View individual workload and task details",
  "delivery.individual.export": "Export individual delivery data",
  "integrations.manage": "Manage connections, sync and attention rules",
  "roles.manage": "Manage role permissions and user access",
};
const roleLabels: Record<Role, string> = {
  user: "User",
  admin: "Administrator",
  super_admin: "Super administrator",
};
export function PermissionsPage() {
  const api = useApi();
  const query = useQuery({
    queryKey: ["permission-policy"],
    queryFn: () =>
      api.get<{ policy: Policy; permissions: string[] }>("/permissions/"),
  });
  return (
    <div className="page scroll-page permissions-page">
      <PageHeader
        title="Roles & permissions"
        description="Control access to delivery data and integration settings without a deployment."
      />
      <Card>
        <p className="section-help">
          Configure delivery, initiative creation, sensitive reporting and
          escalation access by role. Escalation review defaults to Super
          Administrator. Changes take effect on the next API request and are
          audited. Assign accounts to roles deliberately; role names alone do
          not identify management or Board membership.
        </p>
        {query.isPending ? (
          <Loading />
        ) : query.isError ? (
          <ErrorNotice error={query.error} />
        ) : (
          <PermissionForm key={query.data.policy.version} {...query.data} />
        )}
      </Card>
    </div>
  );
}
function PermissionForm({
  policy: initial,
  permissions,
}: {
  policy: Policy;
  permissions: string[];
}) {
  const api = useApi(),
    client = useQueryClient();
  const [policy, setPolicy] = useState(initial),
    [reason, setReason] = useState("");
  const save = useMutation({
    mutationFn: () =>
      api.put("/permissions/", { policy, changeReason: reason }),
    onSuccess: async () => {
      client.removeQueries({ queryKey: ["delivery-details"] });
      await client.invalidateQueries({ queryKey: ["permissions-me"] });
      await client.invalidateQueries({ queryKey: ["permission-policy"] });
      client.removeQueries({ queryKey: ["initiatives"] });
      client.removeQueries({ queryKey: ["dashboard-summary"] });
    },
  });
  return (
    <form
      className="portfolio-form"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <div className="table-wrap">
        <table className="initiative-table">
          <thead>
            <tr>
              <th>Permission</th>
              {Object.values(roleLabels).map((r) => (
                <th key={r}>{r}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {permissions.map((p) => (
              <tr key={p}>
                <td>{labels[p] ?? p}</td>
                {(Object.keys(roleLabels) as Role[]).map((r) => (
                  <td key={r}>
                    <input
                      type="checkbox"
                      aria-label={`${roleLabels[r]}: ${labels[p] ?? p}`}
                      checked={policy.roles[r].includes(p)}
                      onChange={(e) =>
                        setPolicy((v) => ({
                          ...v,
                          roles: {
                            ...v.roles,
                            [r]: e.target.checked
                              ? [...v.roles[r], p]
                              : v.roles[r].filter((s) => s !== p),
                          },
                        }))
                      }
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="section-help">
        Individual viewing requires team-summary access. Exporting requires
        individual viewing. You cannot remove your own access-management
        permission; another authorised manager must make that change.
      </p>
      <label>
        Change reason
        <input
          required
          minLength={3}
          maxLength={1000}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </label>
      <button className="btn-primary" disabled={save.isPending}>
        {save.isPending ? "Saving permissions…" : "Save permissions"}
      </button>
      {save.isError && <ErrorNotice error={save.error} />}
    </form>
  );
}
